<template>
  <div id="assignment-performance">
    <el-row>
      <el-col>
        <el-table
          v-loading.fullscreen.lock="isModalLoading"
          :border="false"
          :cell-class-name="getCellClass"
          :data="performances"
          :default-sort="{ prop: 'score', order: 'descending' }"
          :max-height="600"
          style="width: 100%"
          @cell-mouse-enter="handleHoveringCell"
          @cell-mouse-leave="clearHoveringCell"
        >
          <el-table-column
            :label="$t('school_labels.student_name')"
            :sort-method="sortName"
            :sort-orders="getSortOrders"
            fixed
            min-width="180"
            sortable
          >
            <template slot-scope="scope">
              <div class="user-info">
                <div class="owner-info">
                  <el-link @click="setAssignmentDateAndRedirect(scope.row)" :underline="false">
                    <p>{{ scope.row.user_name }}</p>
                    <p>{{ scope.row.user_email }}</p>
                  </el-link>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('assignments.details.performance_date_complete')"
            :render-header="handleHelpTooltip"
            :sort-orders="getSortOrders"
            fixed
            prop="date_complete"
            sortable
            min-width="180"
          >
            <template slot-scope="scope">
              <div class="date-completed">
                <p class="date">
                  {{ formatDate(scope.row.date_complete) }}
                </p>
                <div class="tag-wrapper">
                  <chip-rounded
                    v-show="scope.row.submission_status"
                    :class="getChipClass(scope.row.submission_status)"
                    class="chip-rounded--small"
                  >
                    {{ scope.row.submission_status }}
                  </chip-rounded>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('assignments.details.performance_score')"
            :render-header="handleHelpTooltip"
            :sort-orders="getSortOrders"
            align="center"
            fixed
            min-width="180"
            prop="score"
            sortable
          >
            <template slot-scope="scope">
              <span>
                {{ formatScore({ score: scope.row.score }) }}
                <el-button
                  v-if="canShowButtonHistoryScore(scope.row)"
                  class="assignment-detail-btn"
                  @click.native.prevent="handleShowUserHistoryScore(scope.row)"
                >
                  {{ $t("classes.classes_details.assessment.table.btn_history") }}
                </el-button>
              </span>
            </template>
          </el-table-column>

          <el-table-column
            v-for="(lesson_id, idx) in lessonIds"
            :key="idx"
            :label="'Lesson ' + (idx + 1) + ' High Score'"
            align="center"
            min-width="150"
            class-name="activity-cell"
          >
            <template slot-scope="scope">
              {{ getHighestScore(lesson_id, scope) }}
            </template>
          </el-table-column>
        </el-table>
      </el-col>
    </el-row>
    <user-assignment-history
      :assignment-type="'lesson'"
      :dialogVisible="showUserHistoryScoreModal"
      :history="selectedUserHistoryScore"
      :title="$t('assignments.modals.assignment_history', { title: currentAssignment.name })"
      :user-info="selectedUser"
      @closed="showUserHistoryScoreModal = false"
    />
  </div>
</template>
<script>
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { DATE_FORMAT, SUBMISSION_LATE, SUBMISSION_MISSED } from "@/constants/generic";
import ChipRounded from "../common/ChipRounded.vue";
import UserAssignmentHistory from "@/components/assignments/UserAssignmentHistory.vue";
import { mapActions, mapGetters } from "vuex";
import { warningMessage } from "@/utils/messages";

export default {
  components: {
    ChipRounded,
    UserAssignmentHistory,
  },
  props: {
    performances: {
      type: Array,
      default: () => [{}],
    },
    dateFormat: {
      type: String,
      default: DATE_FORMAT,
    },
    lessonIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showUserHistoryScoreModal: false,
      selectedUserHistoryScore: {},
      selectedUser: {},
      hoveringId: "",
      isModalLoading: false,
    };
  },
  methods: {
    ...mapActions({
      getUserAssignmentHistory: "assignments/getUserAssignmentHistory",
      setChartDate: "students/setChartDate",
    }),
    formatDate(date) {
      return isNaN(Date.parse(date)) ? date : format(new Date(date), this.dateFormat);
    },
    formatDateWithTimezone(date) {
      if (!date) return "--";
      let timeZone = this.currentOrganization.timezone;
      return utcToZonedTime(date, timeZone);
    },
    formatScore({ score }) {
      return score ? Math.round(score) + "%" : "--";
    },
    getChipClass(status) {
      switch (status) {
        case SUBMISSION_MISSED:
          return "chip-rounded--red";
        case SUBMISSION_LATE:
          return "chip-rounded--orange";
        default:
          return "";
      }
    },
    getCellClass() {
      return "el-table__column--custom";
    },
    sortName(a, b) {
      return a.user_name.toUpperCase().localeCompare(b.user_name.toUpperCase());
    },
    handleHelpTooltip(h, { column }) {
      return h("span", {}, [
        h(
          "el-tooltip",
          {
            attrs: {
              content: this.columnTooltipMap()[column.property],
              effect: "dark",
              placement: "top",
            },
          },
          [h("span", {}, [column.label])]
        ),
      ]);
    },
    columnTooltipMap() {
      return {
        score: this.$t("assignments.details.performance_tooltip.score"),
        date_complete: this.$t("assignments.details.performance_tooltip.date_complete"),
      };
    },
    getHighestScore(lesson_id, scope) {
      let highest_score = null;
      const lesson_highest_score = scope.row.highest_score;
      if (!lesson_highest_score) {
        return "--";
      }
      lesson_highest_score.forEach((lesson, idx) => {
        if (lesson.lesson_id == lesson_id) {
          highest_score = lesson.highest_score;
          // exit loop
          return false;
        }
      });
      return this.formatScore({ score: highest_score });
    },
    handleHoveringCell(row) {
      this.hoveringId = row.user_id;
    },
    clearHoveringCell() {
      this.hoveringId = "";
    },
    canShowButtonHistoryScore(row) {
      return row.score && row.score > 0 && row.user_id === this.hoveringId;
    },
    async handleShowUserHistoryScore(row) {
      this.isModalLoading = true;
      this.selectedUserHistoryScore = {};
      this.selectedUser = row;
      this.selectedUser["lessonIds"] = this.lessonIds;
      try {
        this.selectedUserHistoryScore = await this.getUserAssignmentHistory({
          userId: row.user_id,
          assignmentId: this.currentAssignment.id,
        });
        this.isModalLoading = false;
        this.showUserHistoryScoreModal = true;
      } catch {
        this.isModalLoading = false;
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    setAssignmentDateAndRedirect(row) {
      this.setChartDate({
        startDate: this.formatDateWithTimezone(this.currentAssignment.start_date),
        endDate: this.formatDateWithTimezone(this.currentAssignment.due_date),
      });

      this.$router.push({
        name: "student-details",
        params: {
          organizationId: this.currentClass.organization_id,
          studentId: row.user_id,
        },
      });
    },
  },
  computed: {
    ...mapGetters({
      currentAssignment: "assignments/getCurrentAssignment",
      currentClass: "classes/getCurrentClass",
      currentOrganization: "organization/currentOrganization",
    }),
    getSortOrders() {
      return ["ascending", "descending"];
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../scss/_colors.scss";
.date-completed {
  display: flex;
  align-items: center;
  .date {
    width: 100px;
  }
  .tag-wrapper {
    min-width: 50px;
    margin-left: 10px;
  }
}

.score-cell {
  padding-left: 10px;
}

::v-deep .el-table {
  thead {
    text-transform: inherit;
  }
  .el-table__row {
    [class*="el-table_1_column"] {
      &.el-table__column--custom {
        border-top: none;
        .cell {
          padding-top: 0;
          padding-bottom: 0;
        }
      }
    }
  }
}

::v-deep .chip-rounded--small {
  min-width: 60px;
}

.user-info {
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
  color: $darker-blue;
  .owner-info {
    p {
      font-weight: 500;
      font-size: 16px;
      text-transform: capitalize;
      color: $hyperlink;
      margin: 0;
      text-align: initial;
      display: flex;
      align-items: center;
      &:last-of-type {
        font-size: 14px;
        color: $light-blue-2;
        text-transform: none;
        line-height: 34px;
      }
    }
  }
}
</style>
